<script>
  import Scrolly from "./Scrolly.svelte";
  import katexify from "../katexify";
  import ConfusionMatrix from "./ConfusionMatrix.svelte";
  import { TP, FP, TN, FN, stage } from "./data-store.js";
  import { LayerCake, Svg, Html } from "layercake";
  import { scaleLinear, scaleOrdinal } from "d3-scale";

  import Key from "./Key-html.svelte";
  import AxisX from "./BeeswarmAxisX.svelte";
  import AxisY from "./AxisY.svelte";
  import Beeswarm from "./BeeswarmForce.svelte";
  import DecisionBoundary from "./DecisionBoundary.svelte";

  import { scatterData } from "./datasets.js";

  const xKey = "weight";
  const zKey = "outcome";

  const r = 11;

  const seriesNames = new Set();
  const seriesColors = ["#0f82f2", "#cd0e66"];
  const reScaleX = scaleLinear().domain([0.4, 15]).range([0, 1]);

  const dataTransformed = scatterData.map((d) => {
    seriesNames.add(d[zKey]);

    return {
      [zKey]: d[zKey],
      [xKey]: +reScaleX(+d[xKey]),
      xx: +reScaleX(+d[xKey]),
    };
  });

  // Track metrics
  $: accuracy = ($TP + $TN) / ($TP + $TN + $FP + $FN);
  $: precision = $TP / ($TP + $FP);
  $: recall = $TP / ($TP + $FN);

  // scroll iterator
  let value;

  // Paragraph text for scrolly
  $: steps = [
    `<h1>Accuracy</h1>
     <p>Our cancer detection model gives each patient a score between 0 and 1. We use 0.5 as our cutoff point:
       if someone gets a score above 0.5, we say they might have cancer. If below 0.5, we say they're probably healthy. 
      Think of it like a coin flip - above 50% means "yes", below 50% means "no". 
      (<span class='bold'>Try moving the threshold for yourself!</span>)<br><br>
      Our model's accuracy is:<br><br>
      ${katexify(
        `\\begin{aligned} \\frac{\\mathrm{TP + TN}}{\\mathrm{TP + FP + TN + FN}} = \\frac{${
          $TP + $TN
        } }{${$TP + $FP + $TN + $FN}} \\end{aligned}`
      )} 
       ${katexify(`\\approx ${Math.round(accuracy * 100)}\\%`)}
      </p>`,
    `<h1>Problems with Accuracy</h1>
      <p>Here's a big problem: our data is unbalanced! We have way more healthy people (26) than people with cancer (8).
        This means a lazy model could just say <i>everyone</i> is healthy and still get a high accuracy score of <br><br>
        ${katexify(
          `\\begin{aligned} \\frac{\\mathrm{\\# Healthy\\,People}}{\\mathrm{\\# Total\\,People}} = \\frac{26}{34} = 76\\% \\end{aligned}`
        )}<br><br>
        That's like a spam filter that never catches spam because most emails aren't spam. It looks good on paper, but it's useless! 
         We need better ways to measure how well our model works.
        </p>
      `,
    `<h1>Precision</h1>
      <p>Precision asks: "When our model says someone has cancer, how often is it right?"<br><br>
      ${katexify(
        `\\begin{aligned} \\frac{\\mathrm{Correct\\,Cancer\\,Predictions}}{\\mathrm{All\\,Cancer\\,Predictions}} = \\frac{${$TP} }{${
          $TP + $FP
        }} \\approx ${Math.round(precision * 100)}\\% \\end{aligned}`
      )} <br><br>
      Think of it as trustworthiness - if our model points at someone and says "cancer!", precision tells us how much we can trust that prediction. 
      High precision means fewer false alarms, which matters when wrong predictions cause panic or unnecessary treatments. 
      <span class='bold'>Try moving the threshold</span> to see how precision changes when we get more false alarms.</p>`,
    `<h1>Recall</h1>
      <p>Recall asks: "Of all the people who actually have cancer, how many did we catch?"<br><br>
      ${katexify(
        `\\begin{aligned} \\frac{\\mathrm{People\\,We\\,Caught}}{\\mathrm{People\\,With\\,Cancer}} = \\frac{${$TP} }{${
          $TP + $FN
        }} \\approx ${Math.round(recall * 100)}\\%  \\end{aligned}`
      )} <br><br>
      Think of it like a safety net - recall tells us how good we are at not missing anyone who needs help. 
      High recall means we catch most sick people, which is crucial when missing someone could be life-threatening.
      </p>`,
    `<h1>The Balancing Act</h1>
      <p>We'd love to have perfect precision AND perfect recall, but that's usually impossible. It's like trying to be super careful AND super fast at the same time.
        <span class='bold'>Move the threshold around to see this balancing act in action:</span><br> <br>
        <span class='bold'>Precision (trustworthiness):</span> ${Math.round(precision * 100)}% 
        <br><br>
        <span class='bold'>Recall (catch rate):</span> ${Math.round(recall * 100)}% 
        <br><br>
        </p>`,
  ];

  const target2event = {
    0: () => {
      $stage = "none";
    },
    1: () => {
      $stage = "none";
    },
    2: () => {
      $stage = "precision";
    },
    3: () => {
      $stage = "recall";
    },
    4: () => {
      $stage = "none";
    },
    5: () => {
      $stage = "none";
    },
  };

  // trigger events on scroll typeof lastname !== "undefined"
  // $: if (value) target2event[value]()
  $: if (typeof value !== "undefined") target2event[value]();
</script>

<section>
  <!-- scroll container -->
  <div class="section-container">
    <div class="steps-container">
      <Scrolly bind:value>
        {#each steps as text, i}
          <div class="step" class:active={value === i}>
            <div class="step-content">{@html text}</div>
          </div>
        {/each}
        <div class="spacer" />
      </Scrolly>
    </div>
    <div class="charts-container">
      <!-- <Scatterplot step={value} /> -->
      <div class="chart-container">
        <LayerCake
          padding={{ bottom: 15 }}
          x={xKey}
          y="50"
          z={zKey}
          zScale={scaleOrdinal()}
          zDomain={Array.from(seriesNames)}
          zRange={seriesColors}
          data={dataTransformed}
          let:width
        >
          <Svg>
            <AxisX />
            <AxisY />
            <Beeswarm
              r={width < 400 ? r / 1.15 : r}
              strokeWidth={2}
              xStrength={0.95}
              yStrength={0.075}
            />
            <DecisionBoundary />
          </Svg>

          <Html pointerEvents={false}>
            <Key shape="circle" />
          </Html>
        </LayerCake>
      </div>
      <ConfusionMatrix />
    </div>
  </div>
  <!-- end scroll -->
  <br /><br />
  <p class="body-text">
    This balancing act means you need to think about what matters most for your specific problem.
    <br /><br />
    For cancer detection: A model focused on <span class="bold">recall</span> (high catch rate) will find most sick people and save lives, 
    but might also scare healthy people with false alarms and put them through unnecessary treatments. 
    A model focused on <span class="bold">precision</span> (high trustworthiness) will rarely make false alarms, 
    but might miss some sick people who then don't get the treatment they need.
    <br /><br />
    Since missing cancer can be deadly, we'd probably choose high recall over high precision for this problem.
    <br /><br />
    The key is to decide beforehand: what's worse for your situation - 
    <span class="bold">false alarms</span> or
    <span class="bold">missed cases</span>? Then design your model to minimize whichever one would cause more harm.
  </p>
</section>

<style>
  :global(body) {
    overflow-x: hidden;
    background-color: #ffffff;
  }

  .chart-container {
    width: 95%;
    height: 100%;
  }
  /* space after scroll is finished */
  .spacer {
    height: 40vh;
  }

  .charts-container {
    position: sticky;
    top: 12%;
    display: grid;
    width: 50%;
    grid-template-columns: 100%;
    grid-row-gap: 2rem;
    grid-column-gap: 0rem;
    grid-template-rows: 50% 30%;
    height: 85vh;
    /* border: 2px solid red; */
  }

  .section-container {
    margin-top: 1em;
    text-align: center;
    transition: background 100ms;
    display: flex;
  }

  .step {
    height: 105vh;
    display: flex;
    place-items: center;
    justify-content: center;
  }

  .step-content {
    font-size: 1.1rem;
    background: rgb(255, 255, 255);
    color: #ccc;
    border-radius: 5px;
    padding: 0.5rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    transition: background 500ms ease;
    border: 3px solid var(--squid-ink);
    text-align: left;
    width: 75%;
    margin: auto;
    max-width: 500px;
    font-family: var(--font-main);
    line-height: 1.5;
  }

  .step.active .step-content {
    background: white;
    color: black;
  }

  .steps-container {
    height: 100%;
  }

  .steps-container {
    flex: 1 1 40%;
    z-index: 10;
  }

  /* Comment out the following line to always make it 'text-on-top' */
  @media screen and (max-width: 950px) {
    .section-container {
      flex-direction: column-reverse;
    }

    .steps-container {
      pointer-events: none;
    }

    .charts-container {
      top: 7.5%;
      width: 95%;
      margin: auto;
    }

    .step {
      height: 150vh;
    }

    .step-content {
      width: 95%;
      max-width: 768px;
      font-size: 17px;
      line-height: 1.5;
      padding: 0.5rem;
    }

    .spacer {
      height: 100vh;
    }
  }

  /* responsive height for small screens */
  @media screen and (max-height: 620px) {
    .step {
      height: 170vh;
    }
    .spacer {
      height: 120vh;
    }
  }
</style>
